import React from "react";
import Image from "next/image";
import { SingleProductDescriptionFocusedPrice } from "./SingleProductDescriptionFocusedPrice";

export interface SingleProductProps {
  product: any;
  isRetailMerchant?: boolean;
}

export const SingleProductDescriptionFocused = ({
  product,
  isRetailMerchant,
}: SingleProductProps) => {
  const productImage =
    product.images && product.images.length > 0
      ? product.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, product.images[0])
      : null;

  const formatDuration = (duration: string) => {
    if (!duration) return "";
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?/;
    const matches = duration.match(regex);
    const hours =
      matches?.[1] && parseInt(matches[1]) > 0 ? `${matches[1]} hr` : "";
    const minutes =
      matches?.[2] && parseInt(matches[2]) > 0 ? `${matches[2]} min` : "";
    return `${hours}${hours && minutes ? " " : ""}${minutes}`.trim();
  };

  return (
    <div
      key={product.id}
      data-testid="single-product"
      className={[
        "flex",
        "flex-row",
        "w-full",
        "h-[120px]",
        "shadow-md",
        "rounded-md",
        "relative",
        "cursor-pointer",
        "bg-white",
      ].join(" ")}
    >
      <div
        className={[
          "flex",
          "flex-col",
          "gap-2",
          "w-full",
          "p-4",
          "justify-between",
          "h-auto",
        ].join(" ")}
      >
        <div
          className={["flex", "flex-row", "w-full", "justify-between"].join(
            " "
          )}
        >
          <div
            className={["flex", "flex-row", "w-auto", "justify-between"].join(
              " "
            )}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center overflow-hidden">
              <p
                className={[
                  "font-medium",
                  "text-lg",
                  "flex-1",
                  "truncate",
                  "whitespace-nowrap",
                  "overflow-hidden",
                  "text-ellipsis",
                  "max-w-[200px]",
                  product?.serviceItem
                    ? "md:max-w-[425px]"
                    : "md:max-w-[500px]",
                  product?.serviceItem
                    ? "lg:max-w-[200px]"
                    : "lg:max-w-[300px]",
                  "xl:max-w-[360px]",
                ].join(" ")}
              >
                {product.name}
              </p>
              {product.serviceItem && (
                <span className="text-[14px] font-medium sm:ml-2">
                  &bull;&nbsp;{formatDuration(product.serviceItem.duration)}
                </span>
              )}
            </div>
          </div>
          <SingleProductDescriptionFocusedPrice
            product={product}
            isRetailMerchant={isRetailMerchant}
          />
        </div>
        <p
          className={[
            "font-normal",
            "text-[15px]",
            "leading-6",
            "truncate",
            hasLongWord(product.description) ? "break-all" : "break-words",
            "whitespace-pre-wrap",
            "overflow-hidden",
          ].join(" ")}
        >
          {product.description}
        </p>
      </div>
      {productImage && (
        <div className="relative flex-shrink-0 w-[120px] h-[120px]">
          <Image
            width={120}
            height={120}
            alt={product.imageAlt || "product image"}
            src={productImage.source}
            className="object-cover object-center w-full h-full rounded-r-md"
          />
        </div>
      )}
      {!product.available && (
        <div className="absolute inset-0 bg-white opacity-50 rounded-md"></div>
      )}
    </div>
  );
};

const hasLongWord = (text: string) => {
  return text && text.split(" ").some((word) => word.length > 30);
};
